import {
  Activity,
  BarChart3,
  Bell,
  BriefcaseBusiness,
  CalendarCheck2,
  CalendarDays,
  CheckSquare,
  FolderOpen,
  Handshake,
  Landmark,
  Layers3,
  LayoutDashboard,
  Link2,
  MessageSquare,
  Target,
  Users,
  UsersRound,
  WalletCards,
} from "lucide-react";

export const CANONICAL_ROLES = [
  "admin",
  "finance",
  "payroll",
  "hr",
  "data_manager",
  "marketing_head",
  "marketing_specialist",
  "venue_acquisition_manager",
  "venue_team_lead",
  "venue_specialist",
  "rm_manager",
  "relationship_team_lead",
  "relationship_expert",
  "vendor_manager",
  "vendor_team_lead",
  "vendor_specialist",
];

export const ROLE_ALIASES = {
  acquisition: "venue_specialist",
  vam: "venue_specialist",
  relationship_manager: "relationship_expert",
  relationship_manager_head: "rm_manager",
  rm: "relationship_expert",
  vendor_ops_manager: "vendor_manager",
  vendor_ops_specialist: "vendor_specialist",
  vendor_relationship_manager_head: "vendor_manager",
  vr_head: "vendor_manager",
  vr_team_lead: "vendor_team_lead",
  vr_specialist: "vendor_specialist",
  venue_manager: "venue_specialist",
};

export const normalizeRole = (role = "") => {
  const normalized = String(role || "").trim().toLowerCase();
  return ROLE_ALIASES[normalized] || normalized || "admin";
};

export const getRoleDisplayLabel = (role = "") => {
  const normalized = normalizeRole(role);
  const labels = {
    admin: "Admin",
    finance: "Finance",
    payroll: "Payroll",
    hr: "HR",
    data_manager: "Data Manager",
    marketing_head: "Marketing Head",
    marketing_specialist: "Marketing Specialist",
    venue_acquisition_manager: "Venue Acquisition Managers (Head)",
    venue_team_lead: "Venue Team Lead",
    venue_specialist: "Venue Specialist / Manager",
    rm_manager: "Relationship Manager Head",
    relationship_team_lead: "Relationship Team Lead",
    relationship_expert: "Relationship Manager",
    vendor_manager: "Vendor Relationship Manager Head",
    vendor_team_lead: "VR Team Lead",
    vendor_specialist: "VR Specialist",
  };
  return labels[normalized] || normalized;
};

export const getRoleScopeDefault = (role = "") => {
  const normalized = normalizeRole(role);
  if (["venue_specialist", "relationship_expert", "vendor_specialist"].includes(normalized)) return "my";
  if (["venue_team_lead", "relationship_team_lead", "vendor_manager"].includes(normalized)) return "team";
  return "manager";
};

export const DEFAULT_ROLE_LANDING = {
  admin: "/overview",
  finance: "/finance",
  payroll: "/payroll",
  hr: "/hr",
  data_manager: "/acquisition/review",
  marketing_head: "/overview",
  marketing_specialist: "/overview",
  venue_acquisition_manager: "/acquisition",
  venue_team_lead: "/acquisition",
  venue_specialist: "/acquisition/visit-prep",
  rm_manager: "/rm",
  relationship_team_lead: "/rm",
  relationship_expert: "/rm/mobile",
  vendor_manager: "/vendors",
  vendor_team_lead: "/vendors",
  vendor_specialist: "/vendors",
};

export const ROLE_DESCRIPTIONS = {
  admin: "Broad control-center visibility across all internal operating modules.",
  finance: "Finance-first command surface spanning queue review, settlements, reporting, and collaboration.",
  payroll: "Payroll-first workbench with close coordination to HR and shared operations.",
  hr: "Employee and lifecycle-first operations surface for people workflows.",
  data_manager: "Optional technical fallback role for refinement support; not a required live dependency in the venue-card chain.",
  marketing_head: "Passive role placeholder for HR/Finance UAT alignment only.",
  marketing_specialist: "Passive role placeholder for HR/Finance UAT alignment only.",
  venue_acquisition_manager: "Broad acquisition oversight with manager rollup visibility across venue supply and demand posture.",
  venue_team_lead: "Team-level acquisition coordination view across specialists and daily queue posture.",
  venue_specialist: "Execution-heavy acquisition working view focused on owned leads and follow-through.",
  rm_manager: "Broad RM oversight across conversion, vendors, events, and execution continuity.",
  relationship_team_lead: "Team-level RM coordination across experts, vendors, and event execution posture.",
  relationship_expert: "Execution-heavy RM working view focused on owned opportunities and next actions.",
  vendor_manager: "Vendor and settlement oversight with cross-functional event operations visibility.",
  vendor_team_lead: "Vendor team oversight across approved suppliers and execution follow-through.",
  vendor_specialist: "Vendor and execution-first surface focused on operational coordination.",
};

export const ROLE_OVERVIEW_CONFIG = {
  admin: {
    title: "Internal control center",
    mission: "Oversee acquisition, RM, vendors, settlements, finance, payroll, HR, and shared collaboration from one connected operating engine.",
    primaryRoutes: ["finance", "payroll", "hr", "acquisition", "rm", "vendors", "settlements"],
    secondaryRoutes: ["venus-operations", "reports", "chat-shared", "work-notifications", "work-tasks"],
  },
  finance: {
    title: "Finance command center",
    mission: "Prioritize queue review, settlement visibility, payout posture, and finance collaboration from one connected workflow surface.",
    primaryRoutes: ["finance", "schedule", "settlements", "reports"],
    secondaryRoutes: ["work-documents", "work-notifications", "overview"],
  },
  payroll: {
    title: "Payroll operations desk",
    mission: "Drive pay runs, approvals, exceptions, and people-linked payroll coordination without losing operational context.",
    primaryRoutes: ["payroll", "hr", "chat-shared"],
    secondaryRoutes: ["reports", "work-documents", "overview"],
  },
  hr: {
    title: "People lifecycle desk",
    mission: "Manage employee lifecycle and workforce actions while staying connected to reporting and shared collaboration.",
    primaryRoutes: ["hr", "reports", "chat-shared"],
    secondaryRoutes: ["work-documents", "work-notifications", "overview"],
  },
  venue_acquisition_manager: {
    title: "Venue acquisition manager board",
    mission: "Manager rollup view for acquisition supply, demand posture, and specialist/team-lead execution.",
    primaryRoutes: ["acquisition", "supply-connections", "schedule", "acquisition-brief"],
    secondaryRoutes: ["work-tasks", "overview"],
  },
  venue_team_lead: {
    title: "Venue team lead board",
    mission: "Team-level acquisition coordination view across specialists, due follow-ups, and daily queue posture.",
    primaryRoutes: ["schedule", "supply-connections", "acquisition", "acquisition-pipeline"],
    secondaryRoutes: ["work-tasks", "overview"],
  },
  venue_specialist: {
    title: "Venue specialist board",
    mission: "Execution-focused acquisition working view centered on visit prep, mobile venue capture, and field follow-through.",
    primaryRoutes: ["acquisition-capture", "schedule", "supply-connections", "chat-shared"],
    secondaryRoutes: ["acquisition-pipeline", "reports-acquisition", "overview"],
  },
  rm_manager: {
    title: "RM manager board",
    mission: "Manager rollup view for commercial conversion, vendor continuity, execution posture, and team visibility.",
    primaryRoutes: ["rm", "schedule", "supply-connections", "vendors"],
    secondaryRoutes: ["chat-shared", "work-tasks", "overview"],
  },
  relationship_team_lead: {
    title: "Relationship team lead board",
    mission: "Team-level RM coordination across experts, vendors, event execution, and overdue next actions.",
    primaryRoutes: ["schedule", "rm", "rm-pipeline", "supply-connections"],
    secondaryRoutes: ["reports-rm", "events", "work-tasks"],
  },
  relationship_expert: {
    title: "Relationship expert board",
    mission: "Action-first RM mobile working view for owned opportunities, follow-ups, and fast field logging.",
    primaryRoutes: ["rm-mobile", "schedule", "supply-connections", "chat-shared"],
    secondaryRoutes: ["vendors", "events", "reports-rm"],
  },
  data_manager: {
    title: "Optional refinement support board",
    mission: "Optional technical refinement support role. The live venue-card chain is AI-assisted and does not require a dedicated Data Manager step.",
    primaryRoutes: ["acquisition-review", "acquisition", "chat-shared"],
    secondaryRoutes: ["overview", "work-documents", "venus-operations"],
  },
  vendor_team_lead: {
    title: "VR team lead board",
    mission: "Team-level vendor relationship visibility across supply readiness and event support.",
    primaryRoutes: ["vendors", "events", "chat-shared"],
    secondaryRoutes: ["reports-vendors-performance", "reports-vendors-coverage", "overview"],
  },
  vendor_manager: {
    title: "Vendor oversight board",
    mission: "Manage vendor performance, event coordination, and settlement visibility from one operating view.",
    primaryRoutes: ["vendors", "events", "settlements", "reports"],
    secondaryRoutes: ["chat-shared", "work-notifications", "overview"],
  },
  vendor_specialist: {
    title: "Vendor execution desk",
    mission: "Coordinate vendors and execution issues with fast collaboration access across active workstreams.",
    primaryRoutes: ["vendors", "events", "chat-shared"],
    secondaryRoutes: ["work-tasks", "overview"],
  },
};

const allRoles = [...CANONICAL_ROLES];

const routeRegistry = [
  {
    id: "overview",
    path: "/overview",
    label: "Overview",
    shortLabel: "Overview",
    icon: LayoutDashboard,
    kind: "real",
    parityState: "working",
    visibleTo: allRoles,
    title: "Operations Overview",
    subtitle: "Role-aware command deck for the VenuLoQ internal operating engine.",
    quickActions: [
      { id: "refresh", label: "Refresh", kind: "refresh" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    relatedRoutes: ["chat-shared", "finance", "payroll", "hr"],
  },
  {
    id: "chat-shared",
    path: "/chat/shared",
    label: "Team Chat",
    shortLabel: "Team Chat",
    icon: MessageSquare,
    kind: "real",
    parityState: "working",
    visibleTo: allRoles,
    title: "Shared Team Chat",
    subtitle: "One authenticated collaboration room across every internal module.",
    quickActions: [{ id: "refresh", label: "Refresh", kind: "refresh" }],
    relatedRoutes: ["overview", "finance", "payroll", "hr"],
  },
  {
    id: "work-tasks",
    path: "/work/tasks",
    label: "Tasks",
    shortLabel: "Tasks",
    icon: CheckSquare,
    kind: "real",
    parityState: "working",
    visibleTo: allRoles,
    title: "Shared Tasks Engine",
    subtitle: "Task coordination across acquisition, vendors, finance, payroll, HR, and event execution.",
    quickActions: [{ id: "restoration", label: "Task engine restoration", kind: "restoration" }],
    expectedScope: [
      "Shared internal task engine across modules",
      "Operational ownership and follow-up visibility",
      "Escalation and pending-actions tracking",
      "Role-aware action lists connected to workflows",
    ],
    relatedRoutes: ["overview", "chat-shared"],
  },
  {
    id: "work-task-detail",
    path: "/work/tasks",
    label: "Task Detail",
    shortLabel: "Task Detail",
    icon: CheckSquare,
    kind: "real",
    parityState: "working",
    visibleTo: allRoles,
    hiddenInNav: true,
    title: "Shared Task Detail",
    subtitle: "Cross-module task detail with linked continuity and Team Chat bridge.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["work-tasks", "chat-shared"],
  },
  {
    id: "work-document-detail",
    path: "/work/documents",
    label: "Document Detail",
    shortLabel: "Document Detail",
    icon: FolderOpen,
    kind: "real",
    parityState: "working",
    visibleTo: allRoles,
    hiddenInNav: true,
    title: "Shared Document Detail",
    subtitle: "Document metadata detail with linked continuity and Team Chat bridge.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["work-documents", "chat-shared"],
  },
  {
    id: "work-notification-detail",
    path: "/work/notifications",
    label: "Notification Detail",
    shortLabel: "Notification Detail",
    icon: Bell,
    kind: "real",
    parityState: "working",
    visibleTo: allRoles,
    hiddenInNav: true,
    title: "Shared Notification Detail",
    subtitle: "Notification detail with trigger, source, owner, expected action, and continuity links.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["work-notifications", "chat-shared"],
  },
  {
    id: "work-activity-detail",
    path: "/work/activities",
    label: "Activity Detail",
    shortLabel: "Activity Detail",
    icon: Activity,
    kind: "real",
    parityState: "working",
    visibleTo: allRoles,
    hiddenInNav: true,
    title: "Shared Activity Detail",
    subtitle: "System-generated cross-module activity detail with continuity links.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["work-activities", "chat-shared"],
  },
  {
    id: "work-documents",
    path: "/work/documents",
    label: "Documents",
    shortLabel: "Documents",
    icon: FolderOpen,
    kind: "real",
    parityState: "working",
    visibleTo: allRoles,
    title: "Shared Documents Engine",
    subtitle: "Cross-functional document coordination for finance, HR, acquisition, vendors, and events.",
    quickActions: [{ id: "restoration", label: "Document engine restoration", kind: "restoration" }],
    expectedScope: [
      "Shared document storage and access by role",
      "Workflow-linked document attachments",
      "Document visibility inside hiring, finance, and settlement flows",
      "Operational file access from one connected platform",
    ],
    relatedRoutes: ["overview", "chat-shared", "finance", "hr"],
  },
  {
    id: "work-notifications",
    path: "/work/notifications",
    label: "Notifications",
    shortLabel: "Notifications",
    icon: Bell,
    kind: "real",
    parityState: "working",
    visibleTo: allRoles,
    title: "Shared Notifications Engine",
    subtitle: "Operational alerts and reminders connected across the internal operating engine.",
    quickActions: [{ id: "restoration", label: "Notification engine restoration", kind: "restoration" }],
    expectedScope: [
      "Cross-module notification feed",
      "Issue escalation and no-show alerts",
      "Payout and overdue reminders",
      "Role-aware pending action notifications",
    ],
    relatedRoutes: ["overview", "chat-shared", "settlements"],
  },
  {
    id: "work-activities",
    path: "/work/activities",
    label: "Activities",
    shortLabel: "Activities",
    icon: Activity,
    kind: "real",
    parityState: "working",
    visibleTo: allRoles,
    title: "Shared Activities Engine",
    subtitle: "Chronological activity visibility across hiring, vendors, settlements, acquisition, and execution.",
    quickActions: [{ id: "restoration", label: "Activity engine restoration", kind: "restoration" }],
    expectedScope: [
      "Shared activity feed across modules",
      "Workflow-linked action history",
      "Role-aware activity visibility",
      "Operational chronology for internal teams",
    ],
    relatedRoutes: ["overview", "chat-shared"],
  },
  {
    id: "venus-operations",
    path: "/venus/operations",
    label: "Ven-Us Ops",
    shortLabel: "Ven-Us",
    icon: Activity,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin"],
    title: "Ven-Us Operations Console",
    subtitle: "Admin-only control room for Ven-Us runtime, case x-ray, queues, alerts, audit, and advisory actions.",
    quickActions: [
      { id: "refresh", label: "Refresh", kind: "refresh" },
      { id: "open-overview", label: "Overview", kind: "route", to: "/overview" },
    ],
    relatedRoutes: ["overview", "events", "settlements", "finance", "reports"],
  },
  {
    id: "schedule",
    path: "/schedule",
    label: "Schedule Hub",
    shortLabel: "Schedule",
    icon: CalendarDays,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "finance", "venue_acquisition_manager", "venue_team_lead", "venue_specialist", "rm_manager", "relationship_team_lead", "relationship_expert"],
    title: "Schedule Hub",
    subtitle: "Role-aware calendar foundation for venue visits, RM follow-ups, owner meetings, event dates, and settlement follow-ups.",
    quickActions: [{ id: "open-overview", label: "Overview", kind: "route", to: "/overview" }],
    relatedRoutes: ["acquisition", "rm", "events", "settlements", "finance", "overview"],
  },
  {
    id: "supply-connections",
    path: "/supply/connections",
    label: "Supply Connections",
    shortLabel: "Connections",
    icon: Link2,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "venue_acquisition_manager", "venue_team_lead", "venue_specialist", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager", "vendor_team_lead", "vendor_specialist"],
    title: "Venue ↔ Vendor Connections",
    subtitle: "Operational connection model for preferred vendors, suitability by venue/event context, and weak-linkage visibility.",
    quickActions: [{ id: "open-vendors", label: "Vendors", kind: "route", to: "/vendors" }],
    relatedRoutes: ["acquisition", "vendors", "events", "overview"],
  },
  {
    id: "acquisition",
    path: "/acquisition",
    label: "VAM / Acquisition",
    shortLabel: "Acquisition",
    icon: Target,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "venue_acquisition_manager", "venue_team_lead", "venue_specialist"],
    title: "VAM / Acquisition Dashboard",
    subtitle: "Lead pipeline, follow-up workflow, queue management, and morning prioritization for acquisition teams.",
    quickActions: [
      { id: "visit-prep", label: "Visit Prep", kind: "route", to: "/acquisition/visit-prep" },
      { id: "field-capture", label: "Field Capture", kind: "route", to: "/acquisition/capture/new" },
      { id: "review-queue", label: "Review Queue", kind: "route", to: "/acquisition/review" },
      { id: "open-pipeline", label: "Pipeline", kind: "route", to: "/acquisition/pipeline" },
      { id: "open-reports", label: "Reports", kind: "route", to: "/reports" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    expectedScope: [
      "Acquisition lead pipeline and queue management",
      "Morning brief and lead prioritization behavior",
      "Manager visibility into acquisition performance",
      "Operational stages from New to Booking Confirmed / Lost",
    ],
    relatedRoutes: ["overview", "chat-shared", "reports"],
  },
  {
    id: "acquisition-visit-prep",
    path: "/acquisition/visit-prep",
    label: "Visit Prep",
    shortLabel: "Visit Prep",
    icon: Target,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "venue_acquisition_manager", "venue_team_lead", "venue_specialist"],
    title: "Venue Visit Prep",
    subtitle: "Mobile-first field script showing exactly what to collect before reaching the venue owner.",
    quickActions: [{ id: "start-capture", label: "Start Capture", kind: "route", to: "/acquisition/capture/new" }],
    relatedRoutes: ["acquisition", "acquisition-capture", "acquisition-review"],
  },
  {
    id: "acquisition-capture",
    path: "/acquisition/capture",
    label: "Field Capture",
    shortLabel: "Capture",
    icon: Target,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "venue_acquisition_manager", "venue_team_lead", "venue_specialist"],
    title: "Venue Field Capture",
    subtitle: "Progressive mobile-first venue capture for raw field truth, draft save, and fast review handoff.",
    quickActions: [{ id: "review-queue", label: "Review Queue", kind: "route", to: "/acquisition/review" }],
    relatedRoutes: ["acquisition", "acquisition-visit-prep", "acquisition-review"],
  },
  {
    id: "acquisition-review",
    path: "/acquisition/review",
    label: "Review Queue",
    shortLabel: "Review",
    icon: Target,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "data_manager", "venue_acquisition_manager", "venue_team_lead"],
    title: "Venue Workflow Review Queue",
    subtitle: "Team Lead review, AI-assisted refinement, final Head approval, and owner onboarding routing.",
    quickActions: [{ id: "open-capture", label: "New Capture", kind: "route", to: "/acquisition/capture/new" }],
    relatedRoutes: ["acquisition", "acquisition-capture", "acquisition-visit-prep"],
  },
  {
    id: "acquisition-pipeline",
    path: "/acquisition/pipeline",
    label: "Pipeline",
    shortLabel: "Pipeline",
    icon: Target,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "venue_acquisition_manager", "venue_team_lead", "venue_specialist"],
    title: "Acquisition Pipeline",
    subtitle: "Operational stage pipeline for venue acquisition and follow-up conversion.",
    quickActions: [{ id: "new-lead", label: "New Lead", kind: "restoration" }],
    expectedScope: [
      "New",
      "Contacted",
      "Requirement Understood",
      "Shortlisted",
      "Site Visit",
      "Negotiation",
      "Booking Confirmed",
      "Lost",
    ],
    relatedRoutes: ["acquisition", "reports"],
  },
  {
    id: "acquisition-brief",
    path: "/acquisition/brief",
    label: "Morning Brief",
    shortLabel: "Morning Brief",
    icon: Target,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "venue_acquisition_manager", "venue_team_lead", "venue_specialist"],
    title: "Acquisition Morning Brief",
    subtitle: "Priority-led acquisition briefing view for daily action planning.",
    quickActions: [{ id: "restoration", label: "Morning brief restoration", kind: "restoration" }],
    expectedScope: [
      "Morning lead prioritization",
      "Top follow-ups for the day",
      "Manager summary view",
      "Queue-oriented acquisition execution",
    ],
    relatedRoutes: ["acquisition", "overview"],
  },
  {
    id: "acquisition-visit-prep",
    path: "/acquisition/visit-prep",
    label: "Visit Prep",
    shortLabel: "Visit Prep",
    icon: Target,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "venue_acquisition_manager", "venue_team_lead", "venue_specialist"],
    title: "Venue Visit Prep",
    subtitle: "Mobile-first field script showing exactly what to collect before reaching the venue owner.",
    quickActions: [{ id: "start-capture", label: "Start Capture", kind: "route", to: "/acquisition/capture/new" }],
    relatedRoutes: ["acquisition", "acquisition-capture", "acquisition-review"],
  },
  {
    id: "acquisition-capture",
    path: "/acquisition/capture",
    label: "Field Capture",
    shortLabel: "Capture",
    icon: Target,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "venue_acquisition_manager", "venue_team_lead", "venue_specialist"],
    title: "Venue Field Capture",
    subtitle: "Progressive mobile-first venue capture for raw field truth, draft save, and fast review handoff.",
    quickActions: [{ id: "review-queue", label: "Review Queue", kind: "route", to: "/acquisition/review" }],
    relatedRoutes: ["acquisition", "acquisition-visit-prep", "acquisition-review"],
  },
  {
    id: "acquisition-review",
    path: "/acquisition/review",
    label: "Review Queue",
    shortLabel: "Review",
    icon: Target,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "data_manager", "venue_acquisition_manager", "venue_team_lead"],
    title: "Venue Workflow Review Queue",
    subtitle: "Team Lead review, AI-assisted refinement, final Head approval, and owner onboarding routing.",
    quickActions: [{ id: "open-capture", label: "New Capture", kind: "route", to: "/acquisition/capture/new" }],
    relatedRoutes: ["acquisition", "acquisition-capture", "acquisition-visit-prep"],
  },
  {
    id: "rm",
    path: "/rm",
    label: "RM Operations",
    shortLabel: "RM",
    icon: UsersRound,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_team_lead"],
    title: "RM Operations Dashboard",
    subtitle: "Relationship-management operating surface tied to pipeline, vendors, and event execution.",
    quickActions: [
      { id: "open-mobile", label: "Mobile Desk", kind: "route", to: "/rm/mobile" },
      { id: "open-vendors", label: "Vendors", kind: "route", to: "/vendors" },
      { id: "open-events", label: "Events", kind: "route", to: "/events" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    expectedScope: [
      "RM lead and workflow handling",
      "Pipeline movement visibility",
      "Operational visibility tied to acquisition, event, and vendor work",
      "Role-shaped RM dashboards and execution queues",
    ],
    relatedRoutes: ["overview", "chat-shared", "vendors", "events"],
  },
  {
    id: "rm-mobile",
    path: "/rm/mobile",
    label: "Mobile Desk",
    shortLabel: "Mobile",
    icon: UsersRound,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert"],
    title: "RM Mobile Desk",
    subtitle: "Action-first RM mobile workflow for today’s follow-ups, overdue items, quick calls, notes, and meeting outcomes.",
    quickActions: [{ id: "open-pipeline", label: "Open Pipeline", kind: "route", to: "/rm/pipeline" }],
    relatedRoutes: ["rm", "rm-pipeline", "chat-shared", "vendors", "events"],
  },
  {
    id: "rm-pipeline",
    path: "/rm/pipeline",
    label: "Pipeline",
    shortLabel: "Pipeline",
    icon: UsersRound,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert"],
    title: "RM Pipeline",
    subtitle: "Relationship-management operational flow and movement across active opportunities.",
    quickActions: [
      { id: "open-vendors", label: "Vendors", kind: "route", to: "/vendors" },
      { id: "open-events", label: "Events", kind: "route", to: "/events" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    expectedScope: [
      "RM workflow handling",
      "Pipeline movement tracking",
      "Operational visibility for conversion support",
      "Connected execution and vendor workflows",
    ],
    relatedRoutes: ["rm", "reports"],
  },
  {
    id: "rm-mobile",
    path: "/rm/mobile",
    label: "Mobile Desk",
    shortLabel: "Mobile",
    icon: UsersRound,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert"],
    title: "RM Mobile Desk",
    subtitle: "Action-first RM mobile workflow for today’s follow-ups, overdue items, quick calls, notes, and meeting outcomes.",
    quickActions: [{ id: "open-pipeline", label: "Open Pipeline", kind: "route", to: "/rm/pipeline" }],
    relatedRoutes: ["rm", "rm-pipeline", "chat-shared", "vendors", "events"],
  },
  {
    id: "vendors",
    path: "/vendors",
    label: "Vendor Management",
    shortLabel: "Vendors",
    icon: Handshake,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager", "vendor_team_lead", "vendor_specialist"],
    title: "Vendor Management Dashboard",
    subtitle: "Vendor operations, performance, coverage, shortlisting, and issue escalation visibility.",
    quickActions: [
      { id: "open-directory", label: "Directory", kind: "route", to: "/vendors/directory" },
      { id: "open-performance", label: "Performance", kind: "route", to: "/vendors/performance" },
      { id: "open-events", label: "Events", kind: "route", to: "/events" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    expectedScope: [
      "Vendor dashboard and operational views",
      "Vendor performance visibility",
      "Coverage and usage frequency reporting",
      "Shortlisting and issue/no-show handling",
    ],
    relatedRoutes: ["vendors-directory", "vendors-performance", "vendors-coverage", "events", "settlements", "chat-shared"],
  },
  {
    id: "vendors-directory",
    path: "/vendors/directory",
    label: "Vendor Directory",
    shortLabel: "Directory",
    icon: Handshake,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager", "vendor_team_lead", "vendor_specialist"],
    title: "Vendor Supply Directory",
    subtitle: "Dedicated operational vendor table separated from the vendor home screen.",
    quickActions: [
      { id: "open-home", label: "Vendor Home", kind: "route", to: "/vendors" },
      { id: "open-performance", label: "Performance", kind: "route", to: "/vendors/performance" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    relatedRoutes: ["vendors", "vendors-performance", "vendors-coverage", "events", "settlements", "chat-shared"],
  },
  {
    id: "vendors-performance",
    path: "/vendors/performance",
    label: "Performance",
    shortLabel: "Performance",
    icon: Handshake,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager", "vendor_team_lead", "vendor_specialist"],
    title: "Vendor Performance",
    subtitle: "Operational reporting for vendor effectiveness, quality, and issue patterns.",
    quickActions: [{ id: "restoration", label: "Vendor reporting restoration", kind: "restoration" }],
    expectedScope: [
      "Vendor performance scoring",
      "Usage frequency reporting",
      "Issue/no-show visibility",
      "Operational supplier ranking",
    ],
    relatedRoutes: ["vendors", "reports"],
  },
  {
    id: "vendors-coverage",
    path: "/vendors/coverage",
    label: "Coverage",
    shortLabel: "Coverage",
    icon: Handshake,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager", "vendor_team_lead", "vendor_specialist"],
    title: "Vendor Coverage",
    subtitle: "Coverage and supply-side visibility across operational needs.",
    quickActions: [{ id: "restoration", label: "Coverage restoration", kind: "restoration" }],
    expectedScope: [
      "Vendor coverage visibility",
      "Supply-side reporting",
      "Shortlist confidence views",
      "Geography/category operational coverage",
    ],
    relatedRoutes: ["vendors", "reports"],
  },
  {
    id: "events",
    path: "/events",
    label: "Event Execution",
    shortLabel: "Events",
    icon: CalendarCheck2,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager", "vendor_specialist"],
    title: "Event Execution",
    subtitle: "Execution board, vendor coordination, and event issue visibility for live operations.",
    quickActions: [
      { id: "open-issues", label: "Issues", kind: "route", to: "/events/issues" },
      { id: "open-settlements", label: "Settlements", kind: "route", to: "/settlements" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    expectedScope: [
      "Execution board and coordination views",
      "Vendor execution status handling",
      "Issue escalation visibility",
      "No-show and issue_flagged operations",
    ],
    relatedRoutes: ["vendors", "settlements", "chat-shared"],
  },
  {
    id: "events-execution",
    path: "/events/execution",
    label: "Execution Board",
    shortLabel: "Execution Board",
    icon: CalendarCheck2,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager", "vendor_specialist"],
    title: "Execution Board",
    subtitle: "Per-event coordination and vendor execution tracking.",
    quickActions: [{ id: "new-escalation", label: "New Escalation", kind: "restoration" }],
    expectedScope: [
      "Execution detail views",
      "Vendor execution statuses",
      "Coordination visibility",
      "Escalation triggers for critical issues",
    ],
    relatedRoutes: ["events", "chat-shared"],
  },
  {
    id: "events-issues",
    path: "/events/issues",
    label: "Issues & Escalations",
    shortLabel: "Issues",
    icon: CalendarCheck2,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager", "vendor_specialist"],
    title: "Event Issues & Escalations",
    subtitle: "Operational issue handling for no-show, issue-flagged, and urgent execution failures.",
    quickActions: [{ id: "new-escalation", label: "New Escalation", kind: "restoration" }],
    expectedScope: [
      "Vendor no-show handling",
      "Issue escalation visibility",
      "Urgent execution notifications",
      "Operational remediation workflows",
    ],
    relatedRoutes: ["events", "chat-shared"],
  },
  {
    id: "settlements",
    path: "/settlements",
    label: "Settlements / Payouts",
    shortLabel: "Settlements",
    icon: WalletCards,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "finance", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager"],
    title: "Settlements & Payouts",
    subtitle: "Event settlement visibility, payout queue, and finance/vendor settlement pipeline restoration.",
    quickActions: [
      { id: "open-cases", label: "Cases", kind: "route", to: "/settlements/cases" },
      { id: "open-payouts", label: "Payout Queue", kind: "route", to: "/settlements/payouts" },
      { id: "open-finance", label: "Finance", kind: "route", to: "/finance" },
      { id: "open-reports", label: "Reports", kind: "route", to: "/reports" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    expectedScope: [
      "Settlement system and per-event settlement detail",
      "Payout queue and status pipeline",
      "Commission/payable calculations",
      "Finance visibility into settlements and margins",
    ],
    relatedRoutes: ["settlements-cases", "settlements-payouts", "finance", "events", "vendors", "reports-settlements", "chat-shared"],
  },
  {
    id: "settlements-cases",
    path: "/settlements/cases",
    label: "Settlement Cases",
    shortLabel: "Cases",
    icon: WalletCards,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "finance", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager"],
    title: "Settlement Case Workspace",
    subtitle: "Dedicated settlement case list separated from the settlements home screen.",
    quickActions: [
      { id: "open-home", label: "Settlements Home", kind: "route", to: "/settlements" },
      { id: "open-payouts", label: "Payout Queue", kind: "route", to: "/settlements/payouts" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    relatedRoutes: ["settlements", "settlements-payouts", "events", "finance", "reports-settlements", "chat-shared"],
  },
  {
    id: "settlements-payouts",
    path: "/settlements/payouts",
    label: "Payout Queue",
    shortLabel: "Payout Queue",
    icon: WalletCards,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "finance", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager"],
    title: "Payout Queue",
    subtitle: "Pipeline-style payout visibility across pending, approved, processing, completed, and disputed states.",
    quickActions: [{ id: "export", label: "Export", kind: "restoration" }],
    expectedScope: [
      "Pending",
      "Approved",
      "Processing",
      "Completed",
      "Disputed",
    ],
    relatedRoutes: ["settlements", "finance", "reports"],
  },
  {
    id: "settlement-detail",
    path: "/settlements/events",
    label: "Settlement Detail",
    shortLabel: "Settlement Detail",
    icon: WalletCards,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "finance", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager"],
    hiddenInNav: true,
    title: "Settlement Detail",
    subtitle: "Event-level settlement workspace linking collections, payouts, fulfillment, and closure governance.",
    quickActions: [
      { id: "open-payouts", label: "Payout Queue", kind: "route", to: "/settlements/payouts" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    relatedRoutes: ["settlements", "events", "vendors", "rm", "chat-shared"],
  },
  {
    id: "finance",
    path: "/finance",
    label: "Finance",
    shortLabel: "Finance",
    icon: Landmark,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "finance"],
    title: "Finance Operations",
    subtitle: "Finance queue and approval workflows connected into the shared operating engine.",
    quickActions: [
      { id: "new-finance-item", label: "New Finance Item", kind: "route", to: "/finance" },
      { id: "open-settlements", label: "Settlements", kind: "route", to: "/settlements" },
      { id: "open-reports", label: "Reports", kind: "route", to: "/reports" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    relatedRoutes: ["settlements", "reports", "chat-shared", "work-documents"],
  },
  {
    id: "payroll",
    path: "/payroll",
    label: "Payroll",
    shortLabel: "Payroll",
    icon: BriefcaseBusiness,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "payroll"],
    title: "Payroll Workbench",
    subtitle: "Manual pay run editing, approvals, locking, audit trail, and CSV export.",
    quickActions: [
      { id: "new-pay-run", label: "New Pay Run", kind: "route", to: "/payroll" },
      { id: "open-hr", label: "HR", kind: "route", to: "/hr" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    relatedRoutes: ["hr", "chat-shared", "reports"],
  },
  {
    id: "hr",
    path: "/hr",
    label: "HR",
    shortLabel: "HR",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "payroll", "hr"],
    title: "HR Operating Home",
    subtitle: "Top-level HR operating home linking hiring, onboarding, exits, assets, reviews, analytics, payroll continuity, and shared collaboration.",
    quickActions: [
      { id: "open-employees", label: "Employees", kind: "route", to: "/hr/employees" },
      { id: "open-hiring", label: "Hiring", kind: "route", to: "/hr/hiring" },
      { id: "open-onboarding", label: "Onboarding", kind: "route", to: "/hr/onboarding" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    relatedRoutes: ["payroll", "reports", "chat-shared", "work-documents"],
  },
  {
    id: "hr-employees",
    path: "/hr/employees",
    label: "Employee Master",
    shortLabel: "Employees",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "payroll", "hr"],
    title: "HR Employee Master",
    subtitle: "Dedicated employee workspace separated from the HR home screen for cleaner operations flow.",
    quickActions: [
      { id: "open-hr-home", label: "HR Home", kind: "route", to: "/hr" },
      { id: "open-chat", label: "Open Chat", kind: "chat" },
    ],
    relatedRoutes: ["hr", "payroll", "reports", "chat-shared"],
  },
  {
    id: "reports",
    path: "/reports",
    label: "Reports",
    shortLabel: "Reports",
    icon: BarChart3,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "finance", "payroll", "hr", "venue_acquisition_manager", "venue_team_lead", "venue_specialist", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager", "vendor_specialist"],
    title: "Reports Hub",
    subtitle: "Operational intelligence hub for vendor performance, payouts, margins, HR analytics, and RM conversion reporting.",
    quickActions: [
      { id: "open-finance", label: "Finance", kind: "route", to: "/finance" },
      { id: "open-vendors", label: "Vendors", kind: "route", to: "/vendors" },
      { id: "refresh", label: "Refresh", kind: "refresh" },
    ],
    expectedScope: [
      "Vendor performance reporting",
      "Coverage and usage frequency reporting",
      "Payout summary and overdue visibility",
      "Margins and RM conversion reporting",
    ],
    relatedRoutes: ["overview", "vendors", "settlements", "finance"],
  },
  {
    id: "reports-acquisition",
    path: "/reports/acquisition",
    label: "Acquisition",
    shortLabel: "Acquisition",
    icon: BarChart3,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "venue_acquisition_manager", "venue_team_lead", "venue_specialist"],
    title: "Acquisition Reporting",
    subtitle: "Demand-intake and acquisition pipeline posture.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["acquisition", "chat-shared"],
  },
  {
    id: "reports-rm",
    path: "/reports/rm",
    label: "RM",
    shortLabel: "RM",
    icon: BarChart3,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert"],
    title: "RM Reporting",
    subtitle: "Commercial conversion and RM continuity posture.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["rm", "chat-shared"],
  },
  {
    id: "reports-vendors-performance",
    path: "/reports/vendors/performance",
    label: "Vendor Performance",
    shortLabel: "Vendor Performance",
    icon: BarChart3,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager", "vendor_specialist"],
    title: "Vendor Performance Reporting",
    subtitle: "Usage, issues, no-show, and fulfillment visibility for future shortlisting.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["vendors", "chat-shared"],
  },
  {
    id: "reports-vendors-coverage",
    path: "/reports/vendors/coverage",
    label: "Vendor Coverage",
    shortLabel: "Vendor Coverage",
    icon: BarChart3,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager", "vendor_specialist"],
    title: "Vendor Coverage Reporting",
    subtitle: "Supply posture across cities, categories, and approved inventory.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["vendors", "chat-shared"],
  },
  {
    id: "reports-events",
    path: "/reports/events",
    label: "Event Operations",
    shortLabel: "Event Operations",
    icon: BarChart3,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager", "vendor_specialist"],
    title: "Event Operations Reporting",
    subtitle: "Fulfillment, issue escalation, and operational bottleneck visibility.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["events", "chat-shared"],
  },
  {
    id: "reports-settlements",
    path: "/reports/settlements",
    label: "Settlements",
    shortLabel: "Settlements",
    icon: BarChart3,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "finance", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager"],
    title: "Settlements Reporting",
    subtitle: "Payout, closure, dispute, and ageing visibility from the financial closure engine.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["settlements", "chat-shared"],
  },
  {
    id: "hr-hiring",
    path: "/hr/hiring",
    label: "Hiring",
    shortLabel: "Hiring",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr"],
    title: "HR Hiring",
    subtitle: "Hiring operating surface for requisitions and candidate lifecycle continuity.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["hr-requisitions", "hr-candidates", "chat-shared"],
  },
  {
    id: "hr-requisitions",
    path: "/hr/requisitions",
    label: "Requisitions",
    shortLabel: "Requisitions",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr"],
    title: "HR Requisitions",
    subtitle: "Hiring demand intake and requisition workflow management.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["hr-hiring", "hr-candidates", "chat-shared"],
  },
  {
    id: "hr-requisition-detail",
    path: "/hr/requisitions",
    label: "Requisition Detail",
    shortLabel: "Requisition Detail",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr"],
    hiddenInNav: true,
    title: "Requisition Detail",
    subtitle: "Detailed requisition workspace with linked candidate pipeline.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["hr-requisitions", "hr-candidates", "chat-shared"],
  },
  {
    id: "hr-candidates",
    path: "/hr/candidates",
    label: "Candidates",
    shortLabel: "Candidates",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr"],
    title: "HR Candidates",
    subtitle: "Candidate lifecycle, interview, offer, and conversion continuity.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["hr-requisitions", "hr-hiring", "chat-shared"],
  },
  {
    id: "hr-candidate-detail",
    path: "/hr/candidates",
    label: "Candidate Detail",
    shortLabel: "Candidate Detail",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr"],
    hiddenInNav: true,
    title: "Candidate Detail",
    subtitle: "Candidate conversion and lifecycle detail.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["hr-candidates", "hr-onboarding", "chat-shared"],
  },
  {
    id: "hr-onboarding",
    path: "/hr/onboarding",
    label: "Onboarding",
    shortLabel: "Onboarding",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr", "payroll"],
    title: "HR Onboarding",
    subtitle: "Onboarding queue and employee start readiness continuity.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["payroll", "hr-assets", "chat-shared"],
  },
  {
    id: "hr-onboarding-detail",
    path: "/hr/onboarding",
    label: "Onboarding Detail",
    shortLabel: "Onboarding Detail",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr", "payroll"],
    hiddenInNav: true,
    title: "Onboarding Detail",
    subtitle: "Employee onboarding plan detail and lifecycle continuity.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["hr-onboarding", "hr-assets", "chat-shared"],
  },
  {
    id: "hr-exits",
    path: "/hr/exits",
    label: "Exits",
    shortLabel: "Exits",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr", "payroll"],
    title: "HR Exits",
    subtitle: "Exit approval, asset/F&F gating, and employee closure continuity.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["hr-assets", "payroll", "chat-shared"],
  },
  {
    id: "hr-exit-detail",
    path: "/hr/exits",
    label: "Exit Detail",
    shortLabel: "Exit Detail",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr", "payroll"],
    hiddenInNav: true,
    title: "Exit Detail",
    subtitle: "Employee exit case detail with asset and F&F gating.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["hr-exits", "hr-assets", "chat-shared"],
  },
  {
    id: "hr-assets",
    path: "/hr/assets",
    label: "Assets",
    shortLabel: "Assets",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr", "payroll"],
    title: "HR Assets",
    subtitle: "Asset issue, transfer, return, damage, and clearance continuity.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["hr-onboarding", "hr-exits", "chat-shared"],
  },
  {
    id: "hr-asset-detail",
    path: "/hr/assets",
    label: "Asset Detail",
    shortLabel: "Asset Detail",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr", "payroll"],
    hiddenInNav: true,
    title: "Asset Detail",
    subtitle: "Asset assignment and clearance detail.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["hr-assets", "chat-shared"],
  },
  {
    id: "hr-reviews",
    path: "/hr/reviews",
    label: "Reviews",
    shortLabel: "Reviews",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr"],
    title: "HR Reviews",
    subtitle: "Probation, confirmation, and lifecycle decision workflow.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["hr-analytics", "chat-shared"],
  },
  {
    id: "hr-review-detail",
    path: "/hr/reviews",
    label: "Review Detail",
    shortLabel: "Review Detail",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr"],
    hiddenInNav: true,
    title: "Review Detail",
    subtitle: "Review detail with lifecycle-linked decision making.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["hr-reviews", "chat-shared"],
  },
  {
    id: "hr-analytics",
    path: "/hr/analytics",
    label: "Analytics",
    shortLabel: "Analytics",
    icon: Users,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "hr", "payroll"],
    title: "HR Analytics",
    subtitle: "Operational HR analytics for hiring, onboarding, probation, exits, assets, and reviews.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["reports", "hr", "chat-shared"],
  },
  {
    id: "reports-settlements",
    path: "/reports/settlements",
    label: "Settlements",
    shortLabel: "Settlements",
    icon: BarChart3,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "finance", "rm_manager", "relationship_team_lead", "relationship_expert", "vendor_manager"],
    title: "Settlements Reporting",
    subtitle: "Payout, closure, dispute, and ageing visibility from the financial closure engine.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["settlements", "chat-shared"],
  },
  {
    id: "reports-finance",
    path: "/reports/finance",
    label: "Finance",
    shortLabel: "Finance",
    icon: BarChart3,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin", "finance"],
    title: "Finance Reporting",
    subtitle: "Collections, margins, payouts, disputes, and closure discipline reporting.",
    quickActions: [{ id: "open-chat", label: "Open Chat", kind: "chat" }],
    relatedRoutes: ["finance", "settlements", "chat-shared"],
  },
  {
    id: "chat-shared-poc",
    path: "/poc/shared-chat",
    label: "Shared Chat POC",
    shortLabel: "POC",
    icon: MessageSquare,
    kind: "real",
    parityState: "working",
    visibleTo: ["admin"],
    hiddenInNav: true,
    title: "Shared Team Chat POC",
    subtitle: "Validated core route proving auth persistence and module-to-chat navigation.",
    quickActions: [{ id: "refresh", label: "Refresh", kind: "refresh" }],
    relatedRoutes: ["chat-shared", "overview"],
  },
];

const routeRegistryById = Object.fromEntries(routeRegistry.map((route) => [route.id, route]));

export const NAV_SECTIONS = [
  {
    id: "core",
    label: "Core",
    items: [
      { type: "route", routeId: "overview" },
      { type: "route", routeId: "schedule" },
      { type: "route", routeId: "supply-connections" },
      { type: "route", routeId: "venus-operations" },
      { type: "route", routeId: "chat-shared" },
      {
        type: "group",
        id: "shared-work",
        label: "Shared Work",
        icon: Layers3,
        visibleTo: allRoles,
        children: ["work-tasks", "work-documents", "work-notifications", "work-activities"],
      },
    ],
  },
  {
    id: "revenue-growth",
    label: "Revenue & Growth",
    items: [
      { type: "route-group", routeId: "acquisition", children: ["acquisition-visit-prep", "acquisition-capture", "acquisition-review", "acquisition-pipeline", "acquisition-brief"] },
      { type: "route-group", routeId: "rm", children: ["rm-mobile", "rm-pipeline"] },
    ],
  },
  {
    id: "vendor-event-ops",
    label: "Vendor & Event Ops",
    items: [
      { type: "route-group", routeId: "vendors", children: ["vendors-directory", "vendors-performance", "vendors-coverage"] },
      { type: "route-group", routeId: "events", children: ["events-execution", "events-issues"] },
      { type: "route-group", routeId: "settlements", children: ["settlements-cases", "settlements-payouts"] },
    ],
  },
  {
    id: "operations-functions",
    label: "Operations Functions",
    items: [
      { type: "route", routeId: "finance" },
      { type: "route", routeId: "payroll" },
      { type: "route-group", routeId: "hr", children: ["hr-employees", "hr-hiring", "hr-requisitions", "hr-candidates", "hr-onboarding", "hr-exits", "hr-assets", "hr-reviews", "hr-analytics"] },
    ],
  },
  {
    id: "intelligence",
    label: "Intelligence",
    items: [
      {
        type: "route-group",
        routeId: "reports",
        children: [
          "reports-acquisition",
          "reports-rm",
          "reports-vendors-performance",
          "reports-vendors-coverage",
          "reports-events",
          "reports-settlements",
          "reports-finance",
        ],
      },
    ],
  },
];

const roleCanSee = (visibleTo, role) => visibleTo.includes(normalizeRole(role));

export const getDefaultLandingPath = (role) => DEFAULT_ROLE_LANDING[normalizeRole(role)] || "/overview";

export const getRouteById = (routeId) => routeRegistryById[routeId] || null;

export const getVisibleSections = (role) => {
  const normalizedRole = normalizeRole(role);

  return NAV_SECTIONS.map((section) => {
    const items = section.items
      .map((item) => {
        if (item.type === "route") {
          const route = getRouteById(item.routeId);
          if (!route || route.hiddenInNav || !roleCanSee(route.visibleTo, normalizedRole)) return null;
          return { ...route, navKind: "route" };
        }

        if (item.type === "route-group") {
          const route = getRouteById(item.routeId);
          const children = item.children
            .map((childId) => getRouteById(childId))
            .filter(Boolean)
            .filter((child) => roleCanSee(child.visibleTo, normalizedRole));

          if (!route || !roleCanSee(route.visibleTo, normalizedRole)) return null;
          return { ...route, navKind: "route-group", children };
        }

        if (item.type === "group") {
          const children = item.children
            .map((childId) => getRouteById(childId))
            .filter(Boolean)
            .filter((child) => roleCanSee(child.visibleTo, normalizedRole));
          if (!children.length) return null;
          return {
            id: item.id,
            label: item.label,
            icon: item.icon,
            navKind: "group",
            groupOnly: true,
            children,
          };
        }

        return null;
      })
      .filter(Boolean);

    return { ...section, items };
  }).filter((section) => section.items.length);
};

export const getAccessibleRoutes = (role) => {
  const routes = [];
  getVisibleSections(role).forEach((section) => {
    section.items.forEach((item) => {
      if (item.navKind === "group") {
        routes.push(...item.children);
      } else {
        routes.push(item);
        if (item.children?.length) {
          routes.push(...item.children);
        }
      }
    });
  });
  return routes.filter((route, index, self) => self.findIndex((candidate) => candidate.id === route.id) === index);
};

export const getRouteMeta = (pathname) => {
  const sorted = [...routeRegistry].sort((a, b) => b.path.length - a.path.length);
  return (
    sorted.find((route) => pathname === route.path || pathname.startsWith(`${route.path}/`)) ||
    routeRegistryById.overview
  );
};

export const canRoleAccessPath = (role, pathname) => {
  const route = getRouteMeta(pathname);
  return roleCanSee(route.visibleTo, role);
};

export const PLACEHOLDER_ROUTE_IDS = routeRegistry
  .filter((route) => route.kind === "placeholder")
  .map((route) => route.id);
